'use client';
import { Suspense, useMemo } from 'react';
import Authentication from './Authentication';
import Axios from './Axios';
import Session from './Session';
import CustomToast from './Toast';
import { ModalProvider } from '@/components/common/brand/Modal';
import { MotionLazy } from '@/components/common/animate/motion-lazy';
import AuthGuard from './AuthGuard';
import PermissionRouteGuard from './PermissionRouteGuard';
import { usePathname } from 'next/navigation';
import { ThemeProvider } from '@/theme/theme-provider';
import { CSPostHogProvider } from './PostHog';
import { ImpersonationProvider } from './ImpersonationContext';
import MainContentWrapper from './MainContentWrapper';
import { SettingsProvider } from '@/components/common/settings/context';
import { ThemeTransitionProvider } from '@/components/common/settings/theme-transition';
import { defaultSettings } from '@/components/common/settings/config-settings';
import { DashboardLayout } from '@/components/common/dashboard';
import { SettingsDrawer } from '@/components/common/settings/drawer';
import DigitalAdAxios from './DigitalAdAxios';
import AnalyticsAxios from './AnalyticsAxios';
import AudienceBuilderAxios from './AudienceBuilderAxios';
import PredictionAxios from './PredictionAxios';
import IdentityAxios from './IdentityAxios';
import EmailAxios from './EmailAxios';
import QRCodeAxios from './QRCodeAxios';
import WorkflowAxios from './WorkflowAxios';
import DirectMailAxios from './DirectMailAxios';
import VoiceAxios, { VoiceTokenGate } from './VoiceAxios';
import MailhouseAxios from './MailhouseAxios';
import DesignServiceAxios from './DesignServiceAxios';
import { HeaderProvider } from '@/components/common/dashboard/header-context';
import { SidebarProvider } from '@/app/account/SidebarContext';
import { RBACProvider } from '@/contexts/RBACContext';
import { DuplicateBasePathRepair } from './DuplicateBasePathRepair';
import FeedbackWidget from '@/components/common/FeedbackWidget';

// No `key` here on purpose. AuthContext starts with userId: null and only resolves the
// real value in an effect that runs after first paint — a key derived from userId would
// change right after mount, and a React key change unmounts+remounts everything under it,
// this component's `children` included. That means the entire authenticated app (not just
// this provider) would mount twice on every single page load: once uselessly with no
// identity, then again for real. RBACProvider itself doesn't need a forced remount to reset
// on identity change either — it listens for the impersonation-change/storage events
// (see RBACContext.tsx) and resets+refetches its own state on impersonation start/stop.
// Regular login/logout both do a full page reload (see Authentication.js's clearStorage),
// which already gives RBACProvider a genuinely fresh mount without needing a key.
function RBACShell({ children }) {
  return <RBACProvider>{children}</RBACProvider>;
}

export default function App({ children }) {
  const path = usePathname();

  const showSidebar = useMemo(() => !path?.endsWith('/logic/preview'), [path]);

  // Define public paths that should not show dashboard layout
  // /talk = personalized recipient call links (no dashboard chrome)
  const publicPaths = ['/login', '/register', '/invite', '/reset-password', '/activate-user', '/review-user', '/verification-pending', '/account-disabled', '/talk'];
  const isPublicPath = publicPaths.some(publicPath => path?.includes(publicPath));

  return (
    <SettingsProvider settings={defaultSettings}>
      <MotionLazy>
      <ThemeProvider>
        <ThemeTransitionProvider>
          <ModalProvider>
          <CustomToast>
            <DuplicateBasePathRepair />
            <Authentication>
              <Axios>
                  <IdentityAxios>
                        <AnalyticsAxios>
                              <Session>
                                <ImpersonationProvider>
                                    <AuthGuard>
                                      <Suspense>
                                        {/* {showSidebar && <NewSidebar />} */}
                                      </Suspense>
                                      <CSPostHogProvider>
                                        <MainContentWrapper>
                                          {isPublicPath ? (
                                            // No RBAC — sub-tenant scope inactive inside these providers
                                            <PredictionAxios>
                                              <DigitalAdAxios>
                                                <DirectMailAxios>
                                                  <AudienceBuilderAxios>
                                                    <WorkflowAxios>
                                                      <QRCodeAxios>
                                                        <VoiceAxios>
                                                          <DesignServiceAxios>
                                                            <EmailAxios>{children}</EmailAxios>
                                                          </DesignServiceAxios>
                                                        </VoiceAxios>
                                                      </QRCodeAxios>
                                                    </WorkflowAxios>
                                                  </AudienceBuilderAxios>
                                                </DirectMailAxios>
                                              </DigitalAdAxios>
                                            </PredictionAxios>
                                          ) : (
                                            <HeaderProvider>
                                              <RBACShell>
                                                <WorkflowAxios>
                                                  <PredictionAxios>
                                                    <DigitalAdAxios>
                                                      <DirectMailAxios>
                                                        <AudienceBuilderAxios>
                                                          <QRCodeAxios>
                                                            <VoiceAxios>
                                                              <DesignServiceAxios>
                                                                <EmailAxios>
                                                                  <MailhouseAxios>
                                                                    <PermissionRouteGuard>
                                                                      <SidebarProvider>
                                                                        <DashboardLayout><SettingsDrawer /><VoiceTokenGate>{children}</VoiceTokenGate></DashboardLayout>
                                                                      </SidebarProvider>
                                                                    </PermissionRouteGuard>
                                                                  </MailhouseAxios>
                                                                </EmailAxios>
                                                              </DesignServiceAxios>
                                                            </VoiceAxios>
                                                          </QRCodeAxios>
                                                        </AudienceBuilderAxios>
                                                      </DirectMailAxios>
                                                    </DigitalAdAxios>
                                                  </PredictionAxios>
                                                </WorkflowAxios>
                                              </RBACShell>
                                            </HeaderProvider>
                                          )}
                                          {!isPublicPath && <FeedbackWidget />}
                                        </MainContentWrapper>
                                      </CSPostHogProvider>
                                    </AuthGuard>
                                </ImpersonationProvider>
                              </Session>
                        </AnalyticsAxios>
                    </IdentityAxios>
              </Axios>
            </Authentication>
          </CustomToast>
          </ModalProvider>
        </ThemeTransitionProvider>
      </ThemeProvider>
      </MotionLazy>
    </SettingsProvider>
  );
}
